import { Printer, X } from 'lucide-react';
import React from 'react';
import './PrintKOTPreview.css';

const formatOrderedAt = (timestamp) => {
  if (!timestamp) return '—';
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
};

/** Dummy barcode SVG (vertical bars) for "Scan to Mark food ready" */
const DummyBarcode = () => {
  const bars = [2, 1, 2, 1, 1, 2, 2, 1, 2, 1, 1, 2, 1, 2, 2, 1, 1, 2, 1, 2, 1, 2, 1, 1, 2, 2, 1, 2, 1, 2];
  const width = bars.length * 3;
  return (
    <svg className="kot-barcode-svg" viewBox={`0 0 ${width} 40`} preserveAspectRatio="none">
      {bars.map((w, i) => (
        <rect key={i} x={i * 3} y={0} width={w} height={40} fill="#111" />
      ))}
    </svg>
  );
};

/** Shared slip content for preview and direct print. Uses same classes as PrintKOTPreview.css */
export const KOTSlipContent = ({ order, items = [] }) => {
  if (!order) return null;
  const orderNo = order.orderNo || order.order_no || '—';
  const customerName = order.customerName ?? order.customer_name ?? order.name ?? '—';
  const orderedAt = formatOrderedAt(order.orderPlacedAt || order.ordered_at);
  const platform = order.platform ? String(order.platform).replace(/_/g, ' ').toUpperCase() : '';
  const customerNotes = order.customerNotes ?? order.customer_notes ?? "Don't send cutlery";
  const paymentStatus = order.paymentStatus ?? order.payment_status ?? 'Online Paid';
  const prepareBy = order.prepareBy ?? order.prepare_by ?? formatOrderedAt(order.orderPlacedAt || order.ordered_at || Date.now());
  const deliveryPasscode = order.deliveryPasscode ?? order.delivery_passcode ?? '6759';
  const billNo = order.billNo ?? order.bill_no ?? '—';

  return (
    <div className="kot-paper">
      <header className="kot-header">
        <h1 className="kot-title">KITCHEN ORDER TICKET</h1>
        <p className="kot-platform">
          {platform && <span>{platform}</span>}
          {platform && orderNo && ' · '}
          <span>Order no. {orderNo}</span>
        </p>
      </header>
      <div className="kot-meta">
        <div className="kot-meta-row">
          <span className="kot-meta-label">Name</span>
          <span className="kot-meta-value">{customerName}</span>
        </div>
        <div className="kot-meta-row">
          <span className="kot-meta-label">Ordered at</span>
          <span className="kot-meta-value">{orderedAt}</span>
        </div>
      </div>
      <table className="kot-table">
        <thead>
          <tr>
            <th className="kot-th kot-th-no">#</th>
            <th className="kot-th kot-th-dish">Dish</th>
            <th className="kot-th kot-th-qty">Qty</th>
            <th className="kot-th kot-th-mod">Modifiers / Add-ons</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={index}>
              <td className="kot-td kot-td-no">{index + 1}</td>
              <td className="kot-td kot-td-dish">{item.dishName || '—'}</td>
              <td className="kot-td kot-td-qty">{item.quantity ?? '—'}</td>
              <td className="kot-td kot-td-mod">{item.modifiers || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="kot-divider" aria-hidden="true" />
      <div className="kot-extra">
        <div className="kot-extra-row">
          <span className="kot-extra-label">Customer Notes</span>
          <span className="kot-extra-value">{customerNotes}</span>
        </div>
        <div className="kot-extra-row">
          <span className="kot-extra-label">Payment Status</span>
          <span className="kot-extra-value">{paymentStatus}</span>
        </div>
        <div className="kot-extra-row">
          <span className="kot-extra-label">Prepare by</span>
          <span className="kot-extra-value">{prepareBy}</span>
        </div>
        <div className="kot-extra-row">
          <span className="kot-extra-label">Delivery Passcode</span>
          <span className="kot-extra-value">{deliveryPasscode}</span>
        </div>
      </div>

      <div className="kot-scan-section">
        <h3 className="kot-scan-heading">Scan to Mark food ready</h3>
        <div className="kot-barcode-wrap">
          <DummyBarcode />
        </div>
        <p className="kot-barcode-caption">Pickup barcode for swiggy delivery partner</p>
      </div>

      <footer className="kot-footer">
        <p className="kot-footer-bill">— Bill no. {billNo} —</p>
      </footer>
    </div>
  );
};

/**
 * KOT = Kitchen Order Ticket. Preview modal with Print and Close.
 */
const PrintKOTPreview = ({ order, items = [], onClose }) => {
  if (!order) return null;

  return (
    <div className="kot-preview-overlay" role="dialog" aria-label="KOT print preview">
      <div className="kot-preview-actions">
        <button
          type="button"
          onClick={() => window.print()}
          className="kot-preview-btn kot-preview-btn-print"
        >
          <Printer className="w-5 h-5" />
          Print
        </button>
        <button
          type="button"
          onClick={onClose}
          className="kot-preview-btn kot-preview-btn-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
          Close
        </button>
      </div>
      <div id="kot-print-area" className="kot-print-area">
        <KOTSlipContent order={order} items={items} />
      </div>
    </div>
  );
};

export default PrintKOTPreview;
